// Which messages the reader has genuinely had on screen.
//
// A row counts as read once at least half of it has been inside the viewport
// for DWELL_MS, with the tab in front. A row that scrolls past under the
// wheel never reaches the dwell; a hidden tab reports nothing at all. Each
// message id is reported once per mount — the server ignores repeats anyway,
// but there is no reason to send them.

import { useCallback, useEffect, useRef } from 'react';

export const DWELL_MS = 500;
const VISIBLE_FRACTION = 0.5;

export interface IUseReadObserverOptions {
  /** The scrolling element the rows live in; null until mounted. */
  root: HTMLElement | null;
  /** False while the tab is hidden: timers stop and nothing is reported. */
  enabled: boolean;
  onRead: (messageIds: string[]) => void;
}

/**
 * Returns a callback ref for each row. The row's element must carry
 * `data-message-id`; rows the reader should never report (their own) are
 * simply not registered.
 */
export function useReadObserver({ root, enabled, onRead }: IUseReadObserverOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const reportedRef = useRef(new Set<string>());
  const enabledRef = useRef(enabled);
  const onReadRef = useRef(onRead);

  // Rows registered before the observer exists (first render) wait here.
  const pendingRef = useRef(new Set<Element>());
  // Every row the observer is watching. An observer only speaks when a row's
  // visibility changes, so when the tab comes back each row is watched afresh
  // — that makes the browser report its current state, and a row that stayed
  // on screen the whole time starts its dwell as if it had just scrolled in.
  const watchedRef = useRef(new Set<Element>());

  useEffect(() => {
    onReadRef.current = onRead;
  }, [onRead]);

  useEffect(() => {
    enabledRef.current = enabled;
    if (enabled) {
      const observer = observerRef.current;
      if (!observer) return;
      for (const el of watchedRef.current) {
        observer.unobserve(el);
        observer.observe(el);
      }
      return;
    }
    // Losing the tab cancels every dwell in progress; nothing was seen.
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
  }, [enabled]);

  useEffect(() => {
    if (!root) return;
    const timers = timersRef.current;
    const watched = watchedRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.messageId;
          if (!id || reportedRef.current.has(id)) continue;
          const pending = timers.get(id);
          // `isIntersecting` is true for any overlap; the rule is at least half the row.
          const inView = entry.intersectionRatio >= VISIBLE_FRACTION;
          if (inView && enabledRef.current) {
            if (pending) continue;
            timers.set(id, setTimeout(() => {
              timers.delete(id);
              reportedRef.current.add(id);
              observer.unobserve(entry.target);
              watched.delete(entry.target);
              onReadRef.current([id]);
            }, DWELL_MS));
          } else if (pending) {
            clearTimeout(pending);
            timers.delete(id);
          }
        }
      },
      { root, threshold: VISIBLE_FRACTION },
    );
    observerRef.current = observer;
    for (const el of pendingRef.current) observer.observe(el);
    pendingRef.current.clear();

    return () => {
      observer.disconnect();
      observerRef.current = null;
      watched.clear();
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [root]);

  return useCallback((el: HTMLElement | null) => {
    if (!el) return;
    watchedRef.current.add(el);
    if (observerRef.current) observerRef.current.observe(el);
    else pendingRef.current.add(el);
  }, []);
}
